import { KidBackButton } from '@/shared/ui/KidBackButton';
import '@/shared/ui/kid-chrome.css';

/**
 * The shared KID PAGE HEADER (ADR-0113) — ONE header for every cozy-pastel kid surface (/shop, /wardrobe, /hero,
 * /learn, rewards, savings, wallet…), the kid-side counterpart to the guardian {@link PageHeader}. A compact
 * title, an OPTIONAL one-line `lead` (kid pages SHOW, never lecture in paragraphs), an optional `eyebrow` pill,
 * and the page's spendable `balance` pinned to the end so "what can I spend" sits in the same place everywhere.
 * Replaces the ≈8 hand-rolled page <header>s the audit found. Render inside a `.kid`-scoped page.
 */
export function KidPageHeader({
  title,
  lead,
  eyebrow,
  balance,
  back,
}: {
  title: React.ReactNode;
  /** ONE short line — never a paragraph. Omit when the page needs no explanation. */
  lead?: React.ReactNode;
  /** A small uppercase label / pill above the title (e.g. a section eyebrow). */
  eyebrow?: React.ReactNode;
  /** The spendable currency chip (✨ / coins), pinned to the end — same place across every kid page. */
  balance?: React.ReactNode;
  /**
   * Show the shared `‹` back chevron at the start of the header (for a drilled-in sub-page — NOT a
   * bottom-nav destination). Pass `true` to step back in history, or a path for a deep-linkable page.
   */
  back?: boolean | string;
}): React.ReactElement {
  return (
    <header className="kid-head">
      {back != null && back !== false && (
        <KidBackButton to={typeof back === 'string' ? back : undefined} />
      )}
      <div className="kid-head-text">
        {eyebrow != null && <span className="kid-head-eyebrow">{eyebrow}</span>}
        <h1 className="kid-head-title">{title}</h1>
        {lead != null && <p className="kid-head-lead">{lead}</p>}
      </div>
      {balance != null && <div className="kid-head-balance">{balance}</div>}
    </header>
  );
}
